import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { LabProgress, LabScene } from './labSdk.types'

import { LabRunScenes } from './LabRunScenes.component'
import { Nullable } from 'models/helpers'
import { PhotoStudioGenerating } from '../../../common/PhotoStudioLoading/PhotoStudioGenerating.component'
import { isLabPerspectiveStep } from './labProgressStep'
import styles from './LabLiveOverlay.module.sass'

/** The halo's strength over a Lab run: with no scrim under it any more, it only hints that the run is going */
export const LAB_LIVE_HALO_OPACITY = 0.6
/** How long the straighten grid plays — its animation (LabLiveOverlay.module.sass) */
export const LAB_PERSPECTIVE_SCENE_MS = 1500

interface LabLiveOverlayProps {
  progress: Nullable<LabProgress>
  /** The latest step of the run (the socket's scenes), staged as the Playground does */
  scene?: Nullable<LabScene>
  /** The photo under the overlay: the overlay covers exactly its painted rectangle */
  previewImageUrl?: string
  /** A step's image the scenes put on screen: the result's reveal wipes from it */
  onSceneImage?: (url: string) => void
}

/**
 * The straighten grid: each time the run reports its perspective milestone, or an edit that straightened comes in
 * (`play`); a grid runs to its end
 */
const usePerspectiveScene = (label?: string) => {
  const isPerspective = isLabPerspectiveStep(label)
  const [isPlaying, setIsPlaying] = useState(false)
  const timerRef = useRef<number>()

  const play = useCallback(() => {
    setIsPlaying(true)
    window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => setIsPlaying(false), LAB_PERSPECTIVE_SCENE_MS)
  }, [])

  useEffect(() => {
    if (isPerspective) play()
  }, [isPerspective, play])

  useEffect(() => () => window.clearTimeout(timerRef.current), [])

  return { isPlaying, play }
}

/**
 * What lies over the photo while a Lab run goes: no scrim and no blur — on our own infrastructure the photo itself shows
 * the run — only today's oblique halo, dimmed, and the scenes the feed allows. The progress itself is the run bar in the
 * view band above (LabRunBar). Scenes (lab-back Playground, LabRunScenes): each edited or generated image crossfading
 * in, the straighten grid when the photo is straightened, the zoom on what a detection found; the halo steps aside
 * while one plays.
 */
export const LabLiveOverlay: FC<LabLiveOverlayProps> = ({ progress, scene, previewImageUrl, onSceneImage }) => {
  const straighten = usePerspectiveScene(progress?.rawStage ?? progress?.stage)
  const [isScenePlaying, setIsScenePlaying] = useState(false)

  return (
    <PhotoStudioGenerating
      showText={false}
      showBlurOverlay={false}
      previewImageUrl={previewImageUrl}
      haloOpacity={straighten.isPlaying || isScenePlaying ? 0 : LAB_LIVE_HALO_OPACITY}
      details={(
        <>

          <LabRunScenes scene={scene} photoUrl={previewImageUrl} onPlayingChange={setIsScenePlaying} onStraighten={straighten.play} onImageShown={onSceneImage} />

          {straighten.isPlaying && <span className={styles.perspectiveGrid} data-scene="perspective" aria-hidden="true" />}

        </>
      )}
    />
  )
}
